import { useEffect, useState, useMemo, useCallback } from "react";
import { AlertCircle, X, Filter } from "lucide-react";
import {
  fetchEventTimeline,
  type EventTimelineEntry,
} from "../api/client";
import { fmtDate } from "../utils/format";
import LoadingSpinner from "./LoadingSpinner";

// ---------- types ----------

interface EventTimelineProps {
  dealId: string;
  scenarioId: string;
  tenantId?: string;
  onClose?: () => void;
}

// ---------- event type config ----------

interface EventTypeConfig {
  label: string;
  bg: string;
  text: string;
  border: string;
}

const EVENT_TYPE_MAP: Record<string, EventTypeConfig> = {
  "Lease start": { label: "Lease Start", bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  "Lease expiration": { label: "Expiration", bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  "expiration": { label: "Expiration", bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  "Rent step": { label: "Rent Step", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  "Free rent": { label: "Free Rent", bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-200" },
  "Renewal": { label: "Renewal", bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
  "Absorption": { label: "Absorption", bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200" },
};

function getEventConfig(eventType: string): EventTypeConfig {
  // Try exact match first
  if (EVENT_TYPE_MAP[eventType]) return EVENT_TYPE_MAP[eventType];
  // Try case-insensitive partial match
  const lower = eventType.toLowerCase();
  for (const [key, config] of Object.entries(EVENT_TYPE_MAP)) {
    if (lower.includes(key.toLowerCase())) return config;
  }
  // Default: gray
  return { label: eventType, bg: "bg-gray-50", text: "text-gray-700", border: "border-gray-200" };
}

// ---------- Badge ----------

function EventBadge({ eventType }: { eventType: string }) {
  const config = getEventConfig(eventType);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${config.bg} ${config.text} ${config.border}`}
    >
      {config.label}
    </span>
  );
}

// ---------- Main component ----------

export default function EventTimeline({
  dealId,
  scenarioId,
  tenantId: initialTenantId,
  onClose,
}: EventTimelineProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<EventTimelineEntry[]>([]);

  // Filters
  const [selectedTenant, setSelectedTenant] = useState<string>(initialTenantId ?? "");
  const [enabledTypes, setEnabledTypes] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchEventTimeline(
        dealId,
        scenarioId,
        selectedTenant || undefined,
      );
      setEvents(result);
      // Initialize enabled types from data on first load
      if (enabledTypes.size === 0 && result.length > 0) {
        const types = new Set(result.map((e) => e.event_type));
        setEnabledTypes(types);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load event timeline");
    } finally {
      setLoading(false);
    }
  }, [dealId, scenarioId, selectedTenant]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Derive unique tenants and event types
  const tenantOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of events) {
      if (!map.has(e.tenant_id)) map.set(e.tenant_id, e.tenant_name);
    }
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [events]);

  const allEventTypes = useMemo(() => {
    const set = new Set<string>();
    for (const e of events) set.add(e.event_type);
    return Array.from(set).sort();
  }, [events]);

  const toggleType = (type: string) => {
    setEnabledTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  // Filtered events
  const filtered = useMemo(
    () => events.filter((e) => enabledTypes.has(e.event_type)),
    [events, enabledTypes],
  );

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 text-sm">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        {error}
      </div>
    );
  }

  return (
    <div className="bg-white border border-grove-300 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3 bg-grove-100 border-b border-grove-300 flex items-center justify-between">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-bark-500">
          Event Timeline
          {filtered.length > 0 && (
            <span className="ml-2 text-bark-400 font-normal normal-case">
              ({filtered.length} event{filtered.length !== 1 ? "s" : ""})
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-1.5 rounded-lg transition-colors ${
              showFilters
                ? "bg-forest-100 text-forest-700"
                : "text-bark-400 hover:text-bark-600 hover:bg-grove-200"
            }`}
          >
            <Filter className="w-3.5 h-3.5" />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-bark-400 hover:text-bark-600 hover:bg-grove-200 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="px-5 py-3 bg-grove-50 border-b border-grove-200 space-y-3">
          {/* Tenant dropdown */}
          <div className="flex items-center gap-3">
            <label className="text-[11px] font-medium text-bark-500 whitespace-nowrap">
              Tenant
            </label>
            <select
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
              className="input-sm max-w-[240px]"
            >
              <option value="">All Tenants</option>
              {tenantOptions.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {/* Event type checkboxes */}
          <div className="flex flex-wrap gap-2">
            {allEventTypes.map((type) => {
              const config = getEventConfig(type);
              const checked = enabledTypes.has(type);
              return (
                <label
                  key={type}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border cursor-pointer transition-opacity ${
                    checked
                      ? `${config.bg} ${config.text} ${config.border}`
                      : "bg-gray-50 text-gray-400 border-gray-200 opacity-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={checked}
                    onChange={() => toggleType(type)}
                  />
                  {config.label}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Timeline */}
      {filtered.length === 0 ? (
        <div className="px-5 py-8 text-center">
          <p className="text-sm text-bark-400">No events to display.</p>
        </div>
      ) : (
        <div className="max-h-[500px] overflow-y-auto">
          <div className="divide-y divide-grove-100">
            {filtered.map((event, i) => (
              <div
                key={i}
                className="flex items-start gap-4 px-5 py-3 hover:bg-grove-50/50 transition-colors"
              >
                {/* Date */}
                <div className="flex-shrink-0 w-[80px] text-right">
                  <span className="text-[11px] tabular-nums text-bark-500 font-medium">
                    {fmtDate(event.date)}
                  </span>
                </div>

                {/* Timeline dot */}
                <div className="flex-shrink-0 relative mt-1">
                  <div
                    className={`w-2.5 h-2.5 rounded-full border-2 ${
                      getEventConfig(event.event_type).border
                    } ${getEventConfig(event.event_type).bg}`}
                  />
                  {i < filtered.length - 1 && (
                    <div className="absolute top-3 left-[4px] w-px h-[calc(100%+12px)] bg-grove-200" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <EventBadge eventType={event.event_type} />
                    <span className="text-[11px] font-medium text-bark-700">
                      {event.tenant_name}
                    </span>
                  </div>
                  <p className="text-[11px] text-bark-400 mt-0.5 leading-relaxed">
                    {event.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
